import React from 'react';
import { ReportData } from '../../types/report.types';

interface MethodologyEditorV2Props {
  reportData: ReportData;
  onDataChange: (field: string, value: any) => void;
}

const defaultTestCases = [
  { cat: 'Information gathering', checks: 'Fingerprinting, exposed files/directories, HTTP methods, metadata & version disclosure' },
  { cat: 'Authentication', checks: 'Credential handling, brute-force & lockout, password policy, MFA, password-reset flow' },
  { cat: 'Session management', checks: 'Cookie flags, token entropy, session fixation, timeout/logout, CSRF' },
  { cat: 'Authorization / access control', checks: 'Auth enforcement, IDOR, vertical & horizontal privilege escalation, forced browsing' },
  { cat: 'Input validation', checks: 'SQL injection, XSS (reflected/stored/DOM), command/SSTI/LDAP injection, XXE, SSRF, file upload' },
  { cat: 'Business logic', checks: 'Workflow bypass, parameter tampering, race conditions, rate limiting' },
  { cat: 'Configuration & deployment', checks: 'Security headers, TLS configuration, error handling/info disclosure, default content' },
  { cat: 'Client-side', checks: 'DOM-based XSS, CORS misconfiguration, clickjacking, sensitive data in browser storage' },
  { cat: 'API', checks: 'BOLA/BFLA, mass assignment, excessive data exposure, endpoint auth, rate limiting' },
  { cat: 'Cryptography & data', checks: 'Data in transit/at rest, sensitive data exposure, weak algorithms' },
  { cat: 'Components', checks: 'Outdated libraries / known CVEs, exposed admin or debug interfaces' }
];

const MethodologyEditorV2: React.FC<MethodologyEditorV2Props> = ({ reportData, onDataChange }) => {
  let parsedConfig: any = { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config = parsedConfig;
  const methConfig = config.methodologyV2 || {};

  const targets = reportData.target_details || [];
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  const defaultNetworkTestCases = [
    { cat: 'Information Gathering', checks: 'Host discovery, port scanning, service enumeration, service fingerprinting' },
    { cat: 'Vulnerability Assessment', checks: 'Missing patches, outdated software, default credentials, known vulnerabilities' },
    { cat: 'Exploitation', checks: 'Password spraying, brute-forcing, exploiting public CVEs, remote code execution' },
    { cat: 'Active Directory Attacks', checks: 'Kerberoasting, AS-REP roasting, LLMNR/NBT-NS spoofing, LDAP enumeration' },
    { cat: 'Privilege Escalation', checks: 'LSASS dumping, token manipulation, UAC bypass, credential harvesting' },
    { cat: 'Post-Exploitation', checks: 'Lateral movement, persistence establishment, sensitive data search' }
  ];

  const getPerspective = () => {
    const testingType = (reportData.testing_type || '').toLowerCase();
    if (testingType.includes('black')) return 'black-box';
    if (testingType.includes('white')) return 'white-box';
    if (testingType.includes('grey') || testingType.includes('gray')) return 'grey-box';
    const boxMatch = testingType.match(/(\w+-box)/);
    if (boxMatch) return boxMatch[1];
    return 'grey-box';
  };

  const getMethodologySentence = (meth: any) => {
    let obj: any = {};
    if (typeof meth === 'object' && meth !== null) {
      obj = meth;
    } else if (typeof meth === 'string' && meth.trim().startsWith('{')) {
      try {
        obj = JSON.parse(meth);
      } catch (_) {}
    }
    
    const perspective = getPerspective();
    const parts = [];
    if (obj.web !== false) parts.push('web application penetration testing');
    if (obj.network === true) parts.push('infrastructure and network penetration testing');
    if (obj.sourceCode === true) parts.push('source code security review');
    
    if (parts.length === 0) {
      return `The assessment followed a ${perspective} penetration testing approach aligned to industry security standards and best practices.`;
    }
    
    const scopeStr = parts.join(', ').replace(/,([^,]*)$/, ' and$1');
    return `The assessment was conducted as a ${perspective} security assessment covering ${scopeStr}, aligned to the OWASP Web Security Testing Guide (WSTG) and mapped to the OWASP Top 10 (2021).`;
  };

  const defaultNetworkApproach = 'The assessment was conducted in accordance with the Penetration Testing Execution Standard (PTES), NIST SP 800-115, and industry best practices. Testing was performed from an assumed-breach position on the corporate LAN, combining automated scans with extensive manual analysis to identify vulnerabilities.';

  const getLegacyMethodologyString = (meth: any) => {
    if (typeof meth === 'string') {
      if (meth.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(meth);
          if (parsed && (parsed.web !== undefined || parsed.network !== undefined || parsed.mobile !== undefined || parsed.sourceCode !== undefined)) {
            return '';
          }
        } catch (_) {}
      }
      return meth;
    }
    if (typeof meth === 'object' && meth !== null) {
      const keys = Object.keys(meth);
      if (keys.some(key => key.length > 50)) {
        return keys.join(', ');
      }
    }
    return '';
  };

  const legacyString = getLegacyMethodologyString(reportData.methodology);

  const defaultWebApproach = legacyString ? legacyString : getMethodologySentence(reportData.methodology);

  const defaultApproach = isNetwork ? defaultNetworkApproach : defaultWebApproach;
  const defaultCases = isNetwork ? defaultNetworkTestCases : defaultTestCases;

  const approachText = methConfig.approach !== undefined ? methConfig.approach : defaultApproach;
  const testCases = Array.isArray(methConfig.testCases) ? methConfig.testCases : defaultCases;

  const handleApproachChange = (value: string) => {
    const updated = {
      ...methConfig,
      approach: value
    };
    onDataChange('finding_config', { ...config, methodologyV2: updated });
  };

  const handleTestCaseChange = (index: number, field: 'cat' | 'checks', value: string) => {
    const updatedCases = testCases.map((tc: any, idx: number) =>
      idx === index ? { ...tc, [field]: value } : tc
    );
    onDataChange('finding_config', {
      ...config,
      methodologyV2: {
        ...methConfig,
        testCases: updatedCases
      }
    });
  };

  const handleAddCase = () => {
    onDataChange('finding_config', {
      ...config,
      methodologyV2: {
        ...methConfig,
        testCases: [...testCases, { cat: '', checks: '' }]
      }
    });
  };

  const handleRemoveCase = (index: number) => {
    const updatedCases = testCases.filter((_: any, idx: number) => idx !== index);
    onDataChange('finding_config', {
      ...config,
      methodologyV2: {
        ...methConfig,
        testCases: updatedCases
      }
    });
  };

  const defaultWebPhases = [
    { bold: 'Reconnaissance & information gathering', normal: '' },
    { bold: 'Enumeration & vulnerability identification', normal: '' },
    { bold: 'Manual exploitation & validation', normal: ' - confirming real impact and removing false positives' },
    { bold: 'Post-exploitation & privilege escalation', normal: ' - where in scope' },
    { bold: 'Reporting & remediation guidance', normal: '' }
  ];

  const defaultNetworkPhases = [
    { bold: 'Host discovery', normal: ' - live host identification across in-scope ranges' },
    { bold: 'Port & service enumeration', normal: '' },
    { bold: 'Vulnerability identification', normal: ' (authenticated + unauthenticated)' },
    { bold: 'Exploitation & validation', normal: '' },
    { bold: 'Post-exploitation, lateral movement & Active Directory attacks', normal: '' },
    { bold: 'Privilege escalation to domain dominance', normal: '' },
    { bold: 'Reporting & remediation guidance', normal: '' }
  ];

  const defaultPhases = isNetwork ? defaultNetworkPhases : defaultWebPhases;
  const phases = Array.isArray(methConfig.phases) ? methConfig.phases : defaultPhases;

  const handlePhaseChange = (index: number, field: 'bold' | 'normal', value: string) => {
    const updatedPhases = phases.map((p: any, idx: number) =>
      idx === index ? { ...p, [field]: value } : p
    );
    onDataChange('finding_config', {
      ...config,
      methodologyV2: {
        ...methConfig,
        phases: updatedPhases
      }
    });
  };

  const handleAddPhase = () => {
    onDataChange('finding_config', {
      ...config,
      methodologyV2: {
        ...methConfig,
        phases: [...phases, { bold: 'New Phase', normal: '' }]
      }
    });
  };

  const handleRemovePhase = (index: number) => {
    const updatedPhases = phases.filter((_: any, idx: number) => idx !== index);
    onDataChange('finding_config', {
      ...config,
      methodologyV2: {
        ...methConfig,
        phases: updatedPhases
      }
    });
  };

  return (
    <div className="h-full overflow-y-auto space-y-6">
      {/* Standards & Approach Paragraph */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Standards & Approach Description</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Write the testing standards description</p>
          </div>
        </div>
        <div className="pt-2">
          <textarea
            value={approachText}
            onChange={(e) => handleApproachChange(e.target.value)}
            className="w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
            rows={4}
          />
        </div>
      </div>

      {/* Engagement Phases Bullets */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h3 className="text-base font-bold text-blue-900 tracking-tight">Engagement Phases</h3>
              <p className="text-xs text-blue-700/80 font-medium mt-0.5">Customize the bullet points describing the testing methodology phases</p>
            </div>
          </div>
          <button
            onClick={handleAddPhase}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition shadow-sm"
          >
            Add Phase
          </button>
        </div>

        <div className="space-y-3 pt-2">
          {phases.map((p: any, index: number) => (
            <div key={index} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded p-3 relative group">
              <div className="grid grid-cols-2 gap-3 flex-1">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Phase / Bold Text</label>
                  <input
                    type="text"
                    value={p.bold}
                    onChange={(e) => handlePhaseChange(index, 'bold', e.target.value)}
                    className="w-full px-2.5 py-1 text-xs border border-slate-300 rounded focus:outline-none bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Description / Normal Text</label>
                  <input
                    type="text"
                    value={p.normal}
                    onChange={(e) => handlePhaseChange(index, 'normal', e.target.value)}
                    className="w-full px-2.5 py-1 text-xs border border-slate-300 rounded focus:outline-none bg-white"
                  />
                </div>
              </div>
              <button
                onClick={() => handleRemovePhase(index)}
                className="text-red-600 hover:text-red-800 text-xs font-bold pt-4"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* High-Level Test Cases Table */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h3 className="text-base font-bold text-blue-900 tracking-tight">High-Level Test Cases</h3>
              <p className="text-xs text-blue-700/80 font-medium mt-0.5">Testing categories and checks shown in the PDF</p>
            </div>
          </div>
          <button
            onClick={handleAddCase}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition shadow-sm flex items-center gap-1"
          >
            <span className="text-base leading-none">+</span> Add Row
          </button>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
          <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-blue-700 leading-relaxed">
            These rows are auto-populated from the <strong>generated run checklist</strong> when one exists. If no run checklist was generated for this run, default categories are used. Separate multiple checks with commas.
          </p>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-12 gap-2 px-2 mb-1">
          <div className="col-span-3">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Category</span>
          </div>
          <div className="col-span-8">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Checks Performed (comma-separated)</span>
          </div>
        </div>

        <div className="space-y-2">
          {testCases.map((tc: any, index: number) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg p-2 group hover:border-blue-300 transition-colors">
              {/* Category */}
              <div className="col-span-3">
                <input
                  type="text"
                  value={tc.cat}
                  onChange={(e) => handleTestCaseChange(index, 'cat', e.target.value)}
                  placeholder="Category"
                  className="w-full px-2.5 py-1.5 text-xs font-semibold border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white text-slate-800"
                />
              </div>
              {/* Checks */}
              <div className="col-span-8">
                <textarea
                  value={tc.checks}
                  onChange={(e) => handleTestCaseChange(index, 'checks', e.target.value)}
                  placeholder="e.g. SQL injection, XSS, SSRF, File upload bypass"
                  rows={2}
                  className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white text-slate-700 resize-none leading-relaxed"
                />
              </div>
              {/* Delete */}
              <div className="col-span-1 flex items-center justify-center pt-1">
                <button
                  onClick={() => handleRemoveCase(index)}
                  title="Remove row"
                  className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition opacity-0 group-hover:opacity-100"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
          {testCases.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-xs border border-dashed border-slate-300 rounded-lg">
              No test cases configured. Click <strong>+ Add Row</strong> to add one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MethodologyEditorV2;
